import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { useDispatch, useSelector } from 'core/common/hooks';
import { RootInitialState } from 'core/common/store';
import { evalCondition } from 'core/common/utils/evalCondition';
import { useFeatureFlags } from 'core/feature-flags/hooks';
import { getFeatures, saveFeature } from 'core/feature-flags/store';
import { BaseScreenAttributes, BasePageAttributes, SectionAttributes } from 'core/flow/entities';
import { getConditionAttributes } from 'core/funnel/store';

export const useFlowConditions = () => {
  const dispatch = useDispatch();
  const featureFlags = useFeatureFlags();
  const store = useStore<RootInitialState>();
  const storedFeatures = useSelector(getFeatures);

  const getAttributesByCondition = useCallback(
    <TAttributes extends BaseScreenAttributes | BasePageAttributes | SectionAttributes>(
      attributes: Array<TAttributes>,
    ): TAttributes => {
      const funnelConditions = getConditionAttributes(store.getState());

      const attrsByCondition = attributes.find((attrs) => {
        if (attrs.condition) {
          return [funnelConditions].find(evalCondition(attrs.condition));
        }
      });

      return attrsByCondition ?? attributes[attributes.length - 1];
    },
    [store],
  );

  const getAttributesByFeature = useCallback(
    <TAttributes extends BaseScreenAttributes | BasePageAttributes | SectionAttributes>(
      attributes: Array<TAttributes>,
    ): TAttributes => {
      // TODO remove experiment, use feature
      const attributesWithFeature = attributes.filter((attrs) => attrs.feature || attrs.experiment);
      const attributesWithoutFeature = attributes.filter(
        (attrs) => !attrs.feature || !attrs.experiment,
      );

      if (!attributesWithFeature.length) {
        return getAttributesByCondition(attributes);
      }

      const [featureName] = Object.keys(
        attributesWithFeature[0].feature || attributesWithFeature[0].experiment!,
      );

      const findMatchingAttributes = (value: string) => {
        return attributesWithFeature.find((item) =>
          [{ [featureName]: value }].find(evalCondition(item.feature || item.experiment)),
        );
      };

      const experimentGroup = featureFlags.getExperimentGroup(featureName);

      // Return attributes based on feature from storage
      if (storedFeatures[featureName]) {
        const matchedAttributes = findMatchingAttributes(storedFeatures[featureName]);

        return matchedAttributes ?? attributesWithoutFeature[attributesWithoutFeature.length - 1];
      }

      // Put experiment into the storage if it is not there yet
      if (experimentGroup && !storedFeatures[featureName]) {
        dispatch(saveFeature({ name: featureName, value: experimentGroup as string }));
      }

      const attributesMatchedWithExperimentGroup = findMatchingAttributes(
        experimentGroup as string,
      );

      return attributesMatchedWithExperimentGroup ?? getAttributesByCondition(attributes);
    },
    [dispatch, featureFlags, getAttributesByCondition, storedFeatures],
  );

  return { getAttributesByCondition, getAttributesByFeature };
};
